import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  Typography,
  Autocomplete,
  Paper,
  Collapse,
  IconButton,
  Card,
  CardContent,
  Grid,
  Avatar,
  Fab,
  Zoom,
  Fade,
  Slide,
  Tabs,
  Tab,
  Badge,
} from "@mui/material";
import { 
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Add as AddIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  Sort as SortIcon,
} from "@mui/icons-material";
import {
  Folder as FolderIcon,
  LocalOffer as TagIcon,
  Clear as ClearIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Star as StarIcon,
  Archive as ArchiveIcon,
  Dashboard as DashboardIcon,
  ContactPage as ContactPageIcon,
  FolderOpen as FolderOpenIcon,
} from '@mui/icons-material';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import FallbackLogo from '../components/FallbackLogo';
import makeAPIRequest from "../global/apiCall";
import {
  CONTACT_LIST,
  DELETE,
  DELETE_CONTACT_LIST,
  POST,
} from "../global/apiKeys";
import { useNavigate } from "react-router-dom";
import ListData from "../components/ListData";
import debounce from 'lodash/debounce';
import { makeApiCall } from "../global/apiCall";

function Home() {
  const navigate = useNavigate();
  const { isDark } = useCustomTheme();
  const [logoError, setLogoError] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [contactData, setContactData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [groups, setGroups] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [priority, setPriority] = useState("");
  const [isArchived, setIsArchived] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  const [stats, setStats] = useState({
    total: 0,
    recent: 0,
    priority: 0,
    archived: 0
  });
  const [activeTab, setActiveTab] = useState(0); // 0 = Dashboard, 1 = All Contacts
  const [selectedGroupFromDashboard, setSelectedGroupFromDashboard] = useState(null);
  const loadingRef = useRef(false);

  // Fetch groups and tags
  const fetchGroups = async () => {
    try {
      const response = await makeApiCall('/groups', 'GET');
      if (response.success && Array.isArray(response.data)) {
        setGroups(response.data);
      } else {
        console.error('Groups response is not an array:', response);
        setGroups([]);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      setGroups([]);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await makeApiCall('/tags', 'GET');
      if (response.success && Array.isArray(response.data)) {
        setTags(response.data);
      } else {
        console.error('Tags response is not an array:', response);
        setTags([]);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
      setTags([]);
    }
  };

  const getContactList = useCallback(async (currentPage = page, search = searchQuery, reset = false, customFilters = {}) => {
    if (loadingRef.current || (!hasMore && !reset)) return;

    try {
      loadingRef.current = true;
      setIsLoading(true);
      
      // Use custom filters if provided, otherwise use current state
      const filters = {
        groupId: customFilters.groupId !== undefined ? customFilters.groupId : selectedGroup,
        tagIds: customFilters.tagIds !== undefined ? customFilters.tagIds : selectedTags,
        priority: customFilters.priority !== undefined ? customFilters.priority : priority,
        isArchived: customFilters.isArchived !== undefined ? customFilters.isArchived : isArchived,
      };
      
      const obj = {
        page: currentPage,
        limit: 10,
        search: search || undefined,
        groupId: filters.groupId || undefined,
        tagIds: Array.isArray(filters.tagIds) && filters.tagIds.length > 0 ? filters.tagIds.map(t => t.id || t) : undefined,
        priority: filters.priority !== "" ? filters.priority : undefined,
        isArchived: filters.isArchived || undefined,
      };

      console.log('📡 API Call with filters:', obj);
      const res = await makeAPIRequest(POST, CONTACT_LIST, obj);
      const newContacts = res?.data?.data?.contacts || [];
      const newTotalPages = res?.data?.data?.totalPages || 1;

      console.log('📦 API Response:', { contacts: newContacts.length, totalPages: newTotalPages });

      if (reset) {
        setContactData(newContacts);
        setHasMore(newContacts.length > 0 && currentPage < newTotalPages);
      } else {
        setContactData(prev => [...prev, ...newContacts]);
        setHasMore(newContacts.length > 0 && currentPage < newTotalPages);
      }

      setTotalPages(newTotalPages);
      setPage(currentPage);
    } catch (err) {
      console.error('Error fetching contacts:', err);
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
    }
  }, [searchQuery, selectedGroup, selectedTags, priority, isArchived]);

  const debouncedSearch = useCallback(
    debounce((value) => {
      setPage(1);
      setHasMore(true);
      getContactList(1, value, true);
    }, 500),
    [getContactList]
  );

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleScroll = useCallback(() => {
    if (isLoading || !hasMore) return;

    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const documentHeight = document.documentElement.scrollHeight;
    const windowHeight = window.innerHeight;
    const scrollThreshold = 100; // pixels from bottom

    if (scrollTop + windowHeight >= documentHeight - scrollThreshold) {
      getContactList(page + 1);
    }
  }, [page, isLoading, hasMore, getContactList]);

  const refreshWithFilters = useCallback(() => {
    setPage(1);
    setHasMore(true);
    console.log('🔄 Refreshing with current filters:', {
      groupId: selectedGroup,
      tagIds: selectedTags,
      priority: priority,
      isArchived: isArchived
    });
    getContactList(1, searchQuery, true);
  }, [getContactList, searchQuery, selectedGroup, selectedTags, priority, isArchived]);

  const clearFilters = () => {
    setSelectedGroup("");
    setSelectedTags([]);
    setPriority("");
    setIsArchived(false);
    setSelectedGroupFromDashboard(null);
    setPage(1);
    setHasMore(true);
    
    console.log('🧹 Clearing all filters');
    
    // Pass empty filters directly
    getContactList(1, searchQuery, true, {
      groupId: "",
      tagIds: [],
      priority: "",
      isArchived: false,
    });
  };

  useEffect(() => {
    getContactList(1, "", true);
    fetchGroups();
    fetchTags();
    fetchStats();
  }, []);

  // Update stats when contact data changes
  useEffect(() => {
    if (contactData.length > 0) {
      fetchStats();
    }
  }, [contactData]);

  // Refresh results when filters change
  useEffect(() => {
    if (selectedGroup !== "" || selectedTags.length > 0 || priority !== "" || isArchived) {
      refreshWithFilters();
    }
  }, [selectedGroup, selectedTags, priority, isArchived, refreshWithFilters]);

  useEffect(() => {
    const handleScrollThrottled = () => {
      if (!loadingRef.current) {
        handleScroll();
      }
    };

    window.addEventListener("scroll", handleScrollThrottled);
    window.addEventListener("touchmove", handleScrollThrottled);
    
    return () => {
      window.removeEventListener("scroll", handleScrollThrottled);
      window.removeEventListener("touchmove", handleScrollThrottled);
    };
  }, [handleScroll]);

  const onDeletePress = useCallback((id) => {
    makeAPIRequest(DELETE, DELETE_CONTACT_LIST(id))
      .then(() => {
        setContactData(prev => prev.filter(item => item.id !== id));
      })
      .catch((err) => console.error(err));
  }, []);

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const response = await makeApiCall('/contacts/stats', 'GET');
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Fallback stats from contact data
      setStats({
        total: contactData.length,
        recent: contactData.filter(c => {
          const created = new Date(c.createdAt);
          const week = new Date();
          week.setDate(week.getDate() - 7);
          return created > week;
        }).length,
        priority: contactData.filter(c => c.priority > 0).length,
        archived: contactData.filter(c => c.isArchived).length
      });
    }
  };

  const hasActiveFilters = selectedGroup !== "" || selectedTags.length > 0 || priority !== "" || isArchived;

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle stats card click to filter contacts
  const handleStatsCardClick = (cardType) => {
    // Switch to All Contacts tab
    setActiveTab(1);
    
    // Clear existing filters first
    setSelectedGroup("");
    setSelectedTags([]);
    setSelectedGroupFromDashboard(null);
    setShowFilters(false);
    
    let filterConfig = {
      groupId: "",
      tagIds: [],
      priority: "",
      isArchived: false,
    };
    
    // Apply specific filter based on card type
    switch(cardType) {
      case 'total':
        // Show all contacts (filters already cleared)
        break;
      case 'recent':
        // No specific filter for recent, handled by backend
        break;
      case 'priority':
        filterConfig.priority = 1;
        setPriority(1);
        break;
      case 'archived':
        filterConfig.isArchived = true;
        setIsArchived(true);
        break;
      default:
        break;
    }
    
    // Immediately trigger API call with new filters
    setPage(1);
    setHasMore(true);
    console.log('📈 Stats card clicked:', cardType, 'Filters:', filterConfig);
    
    getContactList(1, searchQuery, true, filterConfig);
  };

  // Handle folder click from dashboard
  const handleFolderClick = (group) => {
    setActiveTab(1); // Switch to All Contacts tab
    
    // Set the filters
    const groupId = group?.id || "";
    setSelectedGroup(groupId);
    setSelectedGroupFromDashboard(group);
    setSelectedTags([]);
    setPriority("");
    setIsArchived(false);
    setShowFilters(false);
    
    // Immediately trigger API call with new filters
    setPage(1);
    setHasMore(true);
    console.log('📁 Folder clicked:', group?.name || 'All Contacts', 'Group ID:', groupId);
    
    // Pass the filters directly to avoid state timing issues
    getContactList(1, searchQuery, true, {
      groupId: groupId,
      tagIds: [],
      priority: "",
      isArchived: false,
    });
  };

  // Stats cards data
  const statsCards = [
    {
      title: 'Total Contacts',
      value: stats.total,
      icon: PeopleIcon,
      color: 'primary.main',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      filterType: 'total',
    },
    {
      title: 'This Week',
      value: stats.recent,
      icon: TrendingUpIcon,
      color: 'success.main', 
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      filterType: 'recent',
    },
    {
      title: 'Priority',
      value: stats.priority,
      icon: StarIcon,
      color: 'warning.main',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      filterType: 'priority',
    },
    {
      title: 'Archived',
      value: stats.archived,
      icon: ArchiveIcon,
      color: 'text.secondary',
      gradient: 'linear-gradient(135deg, #6b7280 0%, #374151 100%)',
      filterType: 'archived',
    },
  ];

  // Dashboard Content Component
  const DashboardContent = () => (
    <Box>
      {/* Welcome Header */}
      <Fade in timeout={800}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {!logoError ? (
                <Box
                  component="img"
                  src="/images/logo.svg"
                  alt="MySocialBizCard Logo"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    animation: 'fadeIn 1s ease-in-out'
                  }}
                  onError={() => setLogoError(true)}
                />
              ) : (
                <FallbackLogo 
                  size={56} 
                  color={isDark ? 'white' : '#667eea'} 
                  showText={true} 
                />
              )}
            </Box>
            <Box>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700, 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 0.5
                }}
              >
                Dashboard
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Overview of your professional network
              </Typography>
            </Box>
          </Box>
        </Box>
      </Fade>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statsCards.map((stat, index) => (
          <Grid item xs={6} sm={3} key={stat.title}>
            <Zoom in timeout={600 + index * 100}>
              <Card
                onClick={() => handleStatsCardClick(stat.filterType)}
                sx={{
                  background: stat.gradient,
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px) scale(1.02)',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {stat.value}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.75rem' }}>
                        {stat.title}
                      </Typography>
                    </Box>
                    <Avatar 
                      sx={{ 
                        bgcolor: 'rgba(255, 255, 255, 0.2)', 
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      <stat.icon />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>
        ))}
      </Grid>

      {/* Groups Dashboard Section */}
      {Array.isArray(groups) && groups.length > 0 && (
        <Slide direction="up" in timeout={900}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              background: isDark
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              border: '1px solid',
              borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            }}
          >
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <FolderIcon color="primary" sx={{ fontSize: 28 }} />
              Contact Folders
            </Typography>
            <Grid container spacing={2}>
              {/* All Contacts Folder */}
              <Grid item xs={6} sm={4} md={3}>
                <Card
                  onClick={() => handleFolderClick(null)}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    background: isDark 
                      ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                      : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                    border: '2px solid',
                    borderColor: 'primary.main',
                    height: '100%', // Match height of other cards
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 20px rgba(102, 126, 234, 0.3)',
                    },
                  }}
                >
                  <CardContent sx={{ 
                    p: 2, 
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}>
                    <Box>
                      <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                      <Typography variant="h6" sx={{ 
                        fontWeight: 600, 
                        mb: 0.5,
                        fontSize: '1rem',
                        minHeight: '1.5rem', // Ensure consistent title height
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        All Contacts
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
                        {stats.total}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        total contacts
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Individual Group Folders */}
              {groups.map((group) => {
                const groupContactCount = contactData.filter(contact => contact.groupId === group.id).length;
                return (
                  <Grid item xs={6} sm={4} md={3} key={group.id}>
                    <Card
                      onClick={() => handleFolderClick(group)}
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        background: isDark 
                          ? 'rgba(255, 255, 255, 0.02)' 
                          : 'rgba(255, 255, 255, 0.5)',
                        height: '100%', // Match height of All Contacts card
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 12px 20px ${group.color}40`,
                          background: `${group.color}10`,
                        },
                      }}
                    >
                      <CardContent sx={{ 
                        p: 2, 
                        textAlign: 'center',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                      }}>
                        <Box>
                          <FolderIcon sx={{ fontSize: 40, color: group.color, mb: 1 }} />
                          <Typography variant="h6" sx={{ 
                            fontWeight: 600, 
                            mb: 0.5,
                            fontSize: '1rem',
                            minHeight: '3rem', // Accommodate group name + default chip + description
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'flex-start'
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                              <span>{group.name}</span>
                              {group.isDefault && (
                                <Chip
                                  label="Default"
                                  size="small"
                                  sx={{ height: 16, fontSize: '0.6rem' }}
                                />
                              )}
                            </Box>
                            {group.description && (
                              <Typography variant="caption" sx={{ 
                                opacity: 0.7, 
                                textAlign: 'center',
                                fontSize: '0.65rem',
                                lineHeight: 1.2,
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}>
                                {group.description}
                              </Typography>
                            )}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: group.color, mb: 0.5 }}>
                            {groupContactCount}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            contact{groupContactCount !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Slide>
      )}
    </Box>
  );

  // All Contacts Content Component
  const AllContactsContent = () => (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700, 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 0.5
          }}
        >
          All Contacts
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {selectedGroupFromDashboard ? `Contacts in "${selectedGroupFromDashboard.name}"` : 'Manage your professional network'}
        </Typography>
      </Box>

      {/* Enhanced Search Bar */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 2, 
          mb: 2, 
          background: isDark 
            ? 'rgba(255, 255, 255, 0.05)' 
            : 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          border: '1px solid',
          borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        }}
      >
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search contacts by name, company, or title..."
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton 
                      onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
                      color="primary"
                      size="small"
                    >
                      {viewMode === 'list' ? <ViewModuleIcon /> : <ViewListIcon />}
                    </IconButton>
                    <IconButton 
                      onClick={() => setShowFilters(!showFilters)}
                      color={hasActiveFilters ? "primary" : "default"}
                      size="small"
                    >
                      <FilterListIcon />
                      {hasActiveFilters && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            width: 8,
                            height: 8,
                            bgcolor: 'error.main',
                            borderRadius: '50%',
                          }}
                        />
                      )}
                    </IconButton>
                  </Box>
                </InputAdornment>
              ),
            }}
          />

          {/* View Mode Toggle & Sort */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip 
                label={`${contactData.length} Contact${contactData.length !== 1 ? 's' : ''}`}
                size="small" 
                variant="outlined"
              />
              {selectedGroupFromDashboard && (
                <Chip
                  label={selectedGroupFromDashboard.name}
                  size="small"
                  sx={{
                    backgroundColor: selectedGroupFromDashboard.color + '20',
                    color: selectedGroupFromDashboard.color,
                  }}
                  onDelete={() => {
                    setSelectedGroup("");
                    setSelectedGroupFromDashboard(null);
                  }}
                />
              )}
              {hasActiveFilters && (
                <Chip 
                  label="Filtered" 
                  size="small" 
                  color="primary"
                  variant="filled"
                  onDelete={clearFilters}
                />
              )}
            </Box>
            
            <Button
              size="small"
              startIcon={<SortIcon />}
              variant="outlined"
              sx={{ minWidth: 'auto' }}
            >
              Sort
            </Button>
          </Box>
        </Paper>

      {/* Enhanced Filters Panel */}
      <Collapse in={showFilters}>
        <Box sx={{ mb: 2 }}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              background: isDark 
                ? 'rgba(255, 255, 255, 0.05)' 
                : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              border: '1px solid',
              borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Advanced Filters</Typography>
              {hasActiveFilters && (
                <Button
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  color="secondary"
                  variant="outlined"
                >
                  Clear All
                </Button>
              )}
            </Box>
            
            <Grid container spacing={2}>
              {/* Group Filter */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter by Group</InputLabel>
                  <Select
                    value={selectedGroup}
                    onChange={(e) => {
                      setSelectedGroup(e.target.value);
                      if (e.target.value === "") {
                        setSelectedGroupFromDashboard(null);
                      }
                    }}
                    startAdornment={selectedGroup && <FolderIcon sx={{ mr: 1, fontSize: 20 }} />}
                  >
                    <MenuItem value="">
                      <em>All Groups</em>
                    </MenuItem>
                    {Array.isArray(groups) && groups.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              backgroundColor: group.color,
                            }}
                          />
                          {group.name}
                          {group.isDefault && <Chip label="Default" size="small" />}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Priority Filter */}
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    <MenuItem value={2}>Very High</MenuItem>
                    <MenuItem value={1}>High</MenuItem>
                    <MenuItem value={0}>Normal</MenuItem>
                    <MenuItem value={-1}>Low</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Status Filter */}
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={isArchived ? "archived" : "active"}
                    onChange={(e) => setIsArchived(e.target.value === "archived")}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="archived">Archived</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Tag Filter */}
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  size="small"
                  options={tags}
                  value={selectedTags}
                  onChange={(event, newValue) => setSelectedTags(newValue)}
                  getOptionLabel={(option) => option.name}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.name}
                        size="small"
                        sx={{
                          bgcolor: option.color + '20',
                          color: option.color,
                          '& .MuiChip-deleteIcon': {
                            color: option.color,
                          }
                        }}
                      />
                    ))
                  }
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ gap: 1 }}>
                      <TagIcon sx={{ color: option.color, fontSize: 16 }} />
                      {option.name}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filter by Tags"
                      placeholder="Select tags to filter contacts..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <TagIcon sx={{ mr: 1, fontSize: 20, color: 'action.active' }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Collapse>

      {/* Contact List */}
      <Box sx={{ pb: 12 }}>
        {contactData.length === 0 && !isLoading ? (
          <Fade in timeout={800}>
            <Paper
              elevation={0}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "400px",
                background: isDark 
                  ? 'rgba(255, 255, 255, 0.05)' 
                  : 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                border: '1px solid',
                borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
              }}
            >
              <img
                src="/images/nodata.svg"
                alt="No contacts found"
                style={{
                  width: "40%",
                  maxWidth: "200px",
                  height: "auto",
                  objectFit: "contain",
                  opacity: 0.6,
                }}
              />
              <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                No contacts found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 300 }}>
                {hasActiveFilters 
                  ? "Try adjusting your filters or clearing them to see all contacts."
                  : "Start building your network by adding your first contact."
                }
              </Typography>
              {!hasActiveFilters && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  sx={{ mt: 2 }}
                  onClick={() => navigate('/create')}
                >
                  Add First Contact
                </Button>
              )}
            </Paper>
          </Fade>
        ) : (
          <>
            <ListData
              contactData={{ contacts: contactData }}
              onDeletePress={onDeletePress}
              onProfilePress={(cardId) => {
                navigate(`/contact-details?cardId=${cardId}`);
              }}
              viewMode={viewMode}
            />
            {isLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", my: 3, pb: 2 }}>
                <CircularProgress size={40} thickness={4} />
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );

  return (
    <Box 
      sx={{ 
        minHeight: "100vh", 
        background: isDark 
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        pb: 10, // Space for bottom nav
      }}
    >
      {/* Navigation Tabs */}
      <Box sx={{ px: 2, pt: 2 }}>
        <Paper 
          elevation={0}
          sx={{
            background: isDark 
              ? 'rgba(255, 255, 255, 0.05)' 
              : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            border: '1px solid',
            borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            borderRadius: 2,
            mb: 3
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                py: 2,
              },
            }}
          >
            <Tab 
              icon={<DashboardIcon />} 
              label="Dashboard" 
              iconPosition="start"
              sx={{ gap: 1 }}
            />
            <Tab 
              icon={
                <Badge 
                  badgeContent={contactData.length} 
                  color="primary"
                  max={999}
                >
                  <ContactPageIcon />
                </Badge>
              } 
              label="All Contacts" 
              iconPosition="start"
              sx={{ gap: 1 }}
            />
          </Tabs>
        </Paper>
      </Box>

      {/* Tab Content */}
      <Box sx={{ px: 2 }}>
        {activeTab === 0 ? <DashboardContent /> : <AllContactsContent />}
      </Box>

      {/* Floating Action Button */}
      <Zoom in timeout={1200}>
        <Fab
          color="primary"
          onClick={() => navigate('/create')}
          sx={{
            position: 'fixed',
            bottom: 100,
            right: 20,
            zIndex: 1000,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a67d8 0%, #667eea 100%)',
              transform: 'scale(1.1)',
            },
          }}
        >
          <AddIcon />
        </Fab>
      </Zoom>
    </Box>
  );
}

export default Home;
